<p-dialog
	[(visible)]="visible"
	[modal]="true"
	[closable]="false"
	[draggable]="false"
	[resizable]="false"
	(onHide)="cancel()"
	[style]="{ width: '480px' }"
	[styleClass]="'payment-method-dialog'"
>
	<ng-template pTemplate="header">
		<div class="dialog-header">
			<span class="dialog-title">{{ (showManualPaymentInstructions || showCryptoPaymentView) ? ('payment.modal.instructionsTitle' | transloco) : ('payment.modal.methodTitle' | transloco) }}</span>
			<button type="button" class="dialog-close" [attr.aria-label]="'common.actions.close' | transloco" (click)="cancel()">
				<lucide-icon name="X" class="icon-em"></lucide-icon>
			</button>
		</div>
	</ng-template>

	<div class="payment-modal-content">
		@if (showCryptoPaymentView) {
			<!-- Crypto Payment View -->
			<div class="crypto-payment-view">
				<div class="instruction-card crypto">
					<div class="crypto-header">
						@if (selectedCryptoBrand; as brand) {
							<app-brand-icon [name]="brand"></app-brand-icon>
						} @else {
							<lucide-icon name="Coins" class="icon-em"></lucide-icon>
						}
						<h3>{{ cryptoCurrencyLabel }}</h3>
					</div>

					@if (!cryptoPaymentDetected && cryptoOrderStatus !== 'EXPIRED') {
						<!-- QR Code -->
						@if (cryptoQrCode) {
							<div class="qr-section">
								<img [src]="cryptoQrCode" [alt]="'common.a11y.qrCode' | transloco" class="qr-code" />
							</div>
						}

						<!-- Payment details -->
						<div class="payment-details">
							<div class="detail-row">
								<div class="detail-left">
									<lucide-icon name="Euro" class="icon-em detail-icon"></lucide-icon>
									<span class="detail-label">{{ 'payment.modal.crypto.total' | transloco }}</span>
								</div>
								<span class="detail-value">{{ totalAmount | appCurrency }}</span>
							</div>
							<div class="detail-row highlight">
								<div class="detail-left">
									<lucide-icon name="Coins" class="icon-em detail-icon"></lucide-icon>
									<span class="detail-label">{{ 'payment.modal.crypto.exactAmount' | transloco }}</span>
								</div>
								<div class="detail-right">
									<span class="detail-value crypto-amount">{{ cryptoAmount }}</span>
									<button type="button" class="btn-copy-inline" [attr.aria-label]="(cryptoAmountCopied ? 'common.a11y.copied' : 'common.a11y.copy') | transloco" (click)="copyCryptoAmount()">
										<lucide-icon [name]="cryptoAmountCopied ? 'Check' : 'Copy'" class="icon-em"></lucide-icon>
									</button>
								</div>
							</div>
							<div class="detail-row">
								<div class="detail-left">
									<lucide-icon name="Wallet" class="icon-em detail-icon"></lucide-icon>
									<span class="detail-label">{{ 'payment.modal.crypto.walletAddress' | transloco }}</span>
								</div>
								<div class="detail-right">
									<span class="detail-value crypto-address">{{ cryptoWalletAddress.substring(0, 12) }}...{{ cryptoWalletAddress.substring(cryptoWalletAddress.length - 8) }}</span>
									<button type="button" class="btn-copy-inline" [attr.aria-label]="(cryptoAddressCopied ? 'common.a11y.copied' : 'common.a11y.copy') | transloco" (click)="copyCryptoAddress()">
										<lucide-icon [name]="cryptoAddressCopied ? 'Check' : 'Copy'" class="icon-em"></lucide-icon>
									</button>
								</div>
							</div>
						</div>

						<!-- Warning -->
						<div class="crypto-warning">
							<lucide-icon name="TriangleAlert" class="icon-em"></lucide-icon>
							<span [innerHTML]="'payment.modal.crypto.warning' | transloco"></span>
						</div>

						<!-- Status indicator -->
						<div class="crypto-status waiting">
							<app-spinner></app-spinner>
							<span>{{ 'payment.modal.crypto.waitingPayment' | transloco }}</span>
						</div>
					} @else if (cryptoPaymentDetected) {
						<!-- Payment detected -->
						<div class="crypto-status success">
							<lucide-icon name="CircleCheck" class="icon-em"></lucide-icon>
							<span>{{ 'payment.modal.crypto.paymentDetected' | transloco }}</span>
						</div>
						<div class="payment-actions">
							<app-button
								[label]="'payment.modal.crypto.close' | transloco"
								(onClick)="close()"
							></app-button>
						</div>
					} @else {
						<!-- Expired -->
						<div class="crypto-status expired">
							<lucide-icon name="Clock" class="icon-em"></lucide-icon>
							<span>{{ 'payment.modal.crypto.expired' | transloco }}</span>
						</div>
						<div class="payment-actions">
							<app-button
								[label]="'payment.modal.crypto.close' | transloco"
								(onClick)="close()"
							></app-button>
						</div>
					}
				</div>

				@if (isCreatingOrder) {
					<div class="creating-order-overlay">
						<app-spinner></app-spinner>
						<p>{{ 'payment.modal.creatingOrder' | transloco }}</p>
					</div>
				}
			</div>
		} @else if (!showManualPaymentInstructions) {
			<!-- Order Summary -->
			<div class="order-summary">
				<div class="summary-row">
					<span class="summary-label">{{ 'payment.modal.product' | transloco }}</span>
					<span class="summary-value">{{ productName }}</span>
				</div>
				<div class="summary-row">
					<span class="summary-label">{{ 'payment.modal.quantity' | transloco }}</span>
					<span class="summary-value">{{ quantity | appNumber }}</span>
				</div>
				@if (originalAmount > 0) {
					<div class="summary-row">
						<span class="summary-label">{{ 'payment.modal.subtotal' | transloco }}</span>
						<span class="summary-value subtotal-strikethrough">{{ originalAmount | appCurrency }}</span>
					</div>
					<div class="summary-row discount">
						<span class="summary-label discount-name">{{ discountName || 'Descuento' }}</span>
						<span class="summary-value discount-amount">-{{ (originalAmount - totalAmount) | appCurrency }}</span>
					</div>
				}
				<div class="summary-row total">
					<span class="summary-label">{{ 'payment.modal.totalToPay' | transloco }}</span>
					<span class="summary-value">{{ totalAmount | appCurrency }}</span>
				</div>
			</div>

			<!-- Payment Methods -->
			<div class="payment-methods">
				<p class="methods-title">{{ 'payment.modal.selectMethod' | transloco }}</p>

				<div class="methods-list">
					@for (method of paymentMethods; track method.id) {
						<div
							class="method-card"
							[class.selected]="selectedMethod === method.id"
							[class.disabled]="!method.available"
							(click)="selectMethod(method)"
						>
							<div class="method-icon">
								@if (method.brandIcon) {
									<app-brand-icon [name]="method.brandIcon"></app-brand-icon>
								} @else {
									<lucide-icon [name]="method.icon" class="icon-em"></lucide-icon>
								}
							</div>
							<div class="method-info">
								<span class="method-name">{{ method.name | transloco }}</span>
								<span class="method-description">{{ method.description | transloco }}</span>
							</div>
							@if (selectedMethod === method.id) {
								<div class="method-check">
									<lucide-icon name="Check" class="icon-em"></lucide-icon>
								</div>
							}
							@if (!method.available) {
								<div class="method-badge">
									<span>{{ 'payment.modal.soon' | transloco }}</span>
								</div>
							}
						</div>
					}
				</div>
			</div>

			<!-- Actions -->
			<div class="payment-actions">
				<app-button
					[label]="'payment.modal.continueToPay' | transloco"
					[disabled]="!selectedMethod || isCreatingOrder"
					[loading]="isCreatingOrder"
					(onClick)="confirmPayment()"
				></app-button>
				<app-button
					[label]="'common.actions.cancel' | transloco"
					variant="secondary"
					(onClick)="cancel()"
				></app-button>
			</div>
		} @else {
			<!-- Manual Payment Instructions -->
			<div class="manual-payment-instructions">
				@if (selectedMethod === 'revolut') {
					<div class="instruction-card revolut">
						<div class="instruction-icon">
							<lucide-icon name="Landmark" class="icon-em"></lucide-icon>
						</div>
						<h3>{{ 'payment.modal.revolut.title' | transloco }}</h3>
						<p class="instruction-text">{{ 'payment.modal.revolut.instructions' | transloco }}</p>

						<div class="payment-details">
							<div class="detail-row">
								<span class="detail-label">{{ 'payment.modal.revolut.revolutId' | transloco }}</span>
								<span class="detail-value">{{'@'}}{{ revolutId }}</span>
							</div>
							<div class="detail-row highlight">
								<span class="detail-label">{{ 'payment.modal.revolut.amount' | transloco }}</span>
								<span class="detail-value">{{ totalAmount | appCurrency }}</span>
							</div>
						</div>

						<!-- QR Code -->
						@if (revolutQrCode) {
							<div class="qr-section">
								<p class="qr-label">{{ 'payment.modal.revolut.scanQr' | transloco }}</p>
								<img [src]="revolutQrCode" [alt]="'common.a11y.qrCodeRevolut' | transloco" class="qr-code" />
							</div>
						}

						<div class="action-divider">
							<span>{{ 'payment.modal.revolut.or' | transloco }}</span>
						</div>

						<app-button
							[label]="'payment.modal.revolut.openApp' | transloco"
							icon="ExternalLink"
							(onClick)="openRevolutApp()"
							class="open-app-button"
						></app-button>

						<p class="fallback-text">
							{{ 'payment.modal.revolut.fallbackText' | transloco }}
							<a [href]="revolutWebLink" target="_blank">revolut.me/{{ revolutId }}</a>
							<br />
							<strong>{{ 'payment.modal.revolut.exactAmount' | transloco:{ amount: (totalAmount | appNumber: '1.2-2') } }}</strong>
						</p>
					</div>
				} @else if (selectedMethod === 'bizum') {
					<div class="instruction-card bizum compact">
						<h3><lucide-icon name="Smartphone" class="icon-em"></lucide-icon> {{ 'payment.modal.bizum.title' | transloco }}</h3>

						<div class="payment-details">
							<div class="detail-row">
								<div class="detail-left">
									<lucide-icon name="Phone" class="icon-em detail-icon"></lucide-icon>
									<span class="detail-label">{{ 'payment.modal.bizum.phone' | transloco }}</span>
								</div>
								<div class="detail-right">
									<span class="detail-value">{{ bizumPhone }}</span>
									<button type="button" class="btn-copy-inline" [attr.aria-label]="(phoneCopied ? 'common.a11y.copied' : 'common.a11y.copy') | transloco" (click)="copyBizumPhone()">
										<lucide-icon [name]="phoneCopied ? 'Check' : 'Copy'" class="icon-em"></lucide-icon>
									</button>
								</div>
							</div>
							<div class="detail-row highlight">
								<div class="detail-left">
									<lucide-icon name="Coins" class="icon-em detail-icon"></lucide-icon>
									<span class="detail-label">{{ 'payment.modal.bizum.amount' | transloco }}</span>
								</div>
								<span class="detail-value">{{ totalAmount | appCurrency }}</span>
							</div>
						</div>
					</div>
				}

				@if (isCreatingOrder) {
					<div class="creating-order-overlay">
						<app-spinner></app-spinner>
						<p>{{ 'payment.modal.creatingOrder' | transloco }}</p>
					</div>
				}

				<div class="confirmation-note compact">
					<lucide-icon name="Info" class="icon-em"></lucide-icon>
					<span>{{ 'payment.modal.confirmNote' | transloco }}</span>
				</div>

				<!-- Actions -->
				<div class="payment-actions">
					<app-button
						[label]="'payment.modal.alreadyPaid' | transloco"
						[disabled]="!orderId || isCreatingOrder"
						(onClick)="confirmManualPayment()"
					></app-button>
				</div>
			</div>
		}
	</div>
</p-dialog>
